import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import RouterView from '@/views/RouterView.vue';
import { useAuthUserStore } from '@/store/use-auth-user-store';
import { useErrorStore } from '@/store/use-error-store';
import { useCurrentOrganizationStore } from '@/store/use-current-organization-store';
import { getFirstPathParam } from '@/utils/common';
import { apiGetOrganizationsList } from '@/api/organizations';
import { storeToRefs } from 'pinia';
import { trackPageChange } from '@/utils/hub-spot';

const blockPromoteWithoutCoOp = () => {
  const currentOrganizationStore = useCurrentOrganizationStore();
  if (!currentOrganizationStore.currentOrganization?.isPromoteEnabled) {
    return `/${currentOrganizationStore.currentOrganization?.slug || ''}`;
  }
};

const routes: RouteRecordRaw[] = [
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('../views/auth/login/Login.vue'),
    meta: {
      authForbidden: true,
    },
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('../views/auth/reset-password/ResetPassword.vue'),
    meta: {
      authForbidden: true,
    },
  },
  {
    path: '/auth/set-new-password',
    name: 'set-new-password',
    component: () =>
      import('../views/auth/set-new-password/SetNewPassword.vue'),
    meta: {
      authForbidden: true,
    },
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('../views/auth/register/Register.vue'),
    meta: {
      authForbidden: true,
    },
  },
  {
    path: '/auth/logout',
    name: 'logout',
    component: () => import('../views/auth/logout/Logout.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/auth/sso',
    name: 'sso',
    component: () => import('../views/auth/sso/Sso.vue'),
  },
  {
    path: '',
    component: () => import('../layouts/main-layout/main-layout.vue'),
    async beforeEnter(to) {
      if (to.path === '/') {
        const { nodes: organizations } = await apiGetOrganizationsList({
          perPage: 1,
        });
        return `/${organizations[0].slug}`;
      }
    },
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '/:organizationSlug',
        component: RouterView,
        children: [
          {
            path: '',
            name: 'root',
            redirect: (to) => {
              const { currentOrganization } = storeToRefs(
                useCurrentOrganizationStore(),
              );
              const base = `/${to.params.organizationSlug}`;
              return currentOrganization.value?.displayQuestionnaire
                ? `${base}/key-metrics`
                : `${base}/content`;
            },
          },
          {
            path: 'key-metrics',
            name: 'keyMetrics',
            component: () => import('../views/key-metrics/KeyMetrics.vue'),
          },
          {
            path: 'campaigns',
            name: 'campaignsOverview',
            component: () =>
              import('../views/campaigns/campaign-overview/campaign-overview.vue'),
          },
          {
            path: 'campaigns/create',
            name: 'createCampaign',
            component: () =>
              import('../views/campaigns/create-campaign/create-campaign.vue'),
          },
          {
            path: 'campaigns/:campaignId/details',
            name: 'campaignDetails',
            component: () =>
              import('../views/campaigns/campaign-details/campaign-details.vue'),
            props: true,
          },
          {
            path: 'campaigns/:campaignId/edit',
            name: 'editCampaign',
            component: () =>
              import('../views/campaigns/edit-campaign/edit-campaign.vue'),
            props: true,
          },
          {
            path: 'content',
            name: 'contentOverview',
            component: () =>
              import('../views/content/content-overview/ContentOverview.vue'),
          },
          {
            path: 'content/create/resource',
            name: 'createResourceContent',
            component: () =>
              import('../views/content/create-content/CreateContent.vue'),
          },
          {
            path: 'content/create/scorm-course',
            name: 'createScormCourseContent',
            component: () =>
              import('../views/content/create-content/CreateContent.vue'),
          },
          {
            path: 'content/create/socials',
            name: 'createSocialsContent',
            component: () =>
              import('../views/content/create-content/CreateContent.vue'),
          },
          {
            path: 'content/create/files',
            name: 'createFilesContent',
            component: () =>
              import('../views/content/create-content/CreateContent.vue'),
          },
          {
            path: 'content/:contentId/details',
            name: 'contentDetails',
            props: true,
            component: () =>
              import('../views/content/content-details/ContentDetails.vue'),
          },
          {
            path: 'content/:contentId/edit',
            name: 'editContent',
            props: true,
            component: () =>
              import('../views/content/edit-content/EditContent.vue'),
          },
          {
            path: 'hubs',
            name: 'hubsOverview',
            component: () =>
              import(
                /* webpackChunkName: "hubsOverview" */ '../views/hubs/hubs-overview/HubsOverview.vue'
              ),
          },
          {
            path: 'hubs/create',
            name: 'createHub',
            component: () =>
              import(
                /* webpackChunkName: "createHub" */ '../views/hubs/create-hub/CreateHub.vue'
              ),
          },
          {
            path: 'hubs/:hubId/edit',
            props: true,
            name: 'editHub',
            component: () =>
              import(
                /* webpackChunkName: "editHub" */ '../views/hubs/edit-hub/EditHub.vue'
              ),
          },
          {
            path: 'notification-emails',
            name: 'notificationEmailOverview',
            component: () =>
              import('../views/notification-email/notification-email-overview/notification-email-overview.vue'),
          },
          {
            path: 'notification-emails/:notificationEmailId/details',
            props: true,
            name: 'notificationEmailDetails',
            component: () =>
              import('../views/notification-email/notification-email-details/notification-email-details.vue'),
          },
          {
            path: 'notification-emails/create',
            name: 'createNotificationEmail',
            component: () =>
              import('../views/notification-email/create-notification-email/create-notification-email.vue'),
          },
          {
            path: 'notification-emails/:notificationEmailId/edit',
            name: 'editNotificationEmail',
            props: true,
            component: () =>
              import('../views/notification-email/edit-notification-email/edit-notification-email.vue'),
          },
          {
            path: 'notification-emails/:notificationEmailId/duplicate',
            name: 'duplicateNotificationEmail',
            props: true,
            component: () =>
              import('../views/notification-email/duplicate-notification-email/duplicate-notification-email.vue'),
          },

          {
            path: 'planning',
            name: 'planningOverview',
            component: () =>
              import('../views/planning/calendar/PlanningCalendar.vue'),
          },
          {
            path: 'planning/create',
            name: 'createPlanning',
            component: () =>
              import('../views/planning/create-event/CreateEvent.vue'),
          },
          {
            path: 'planning/details/:eventId',
            name: 'eventDetails',
            component: () =>
              import('../views/planning/event-details/EventDetails.vue'),
            props: (route) => ({ eventId: route.params.eventId }),
          },
          {
            path: 'planning/edit/:eventId',
            name: 'editEvent',
            component: () =>
              import('../views/planning/edit-event/EditEvent.vue'),
            props: (route) => ({ eventId: route.params.eventId }),
          },
          {
            path: 'planning/calendar',
            name: 'planningCalendar',
            props: true,
            component: () =>
              import('../views/planning/calendar/PlanningCalendar.vue'),
          },
          {
            path: 'settings',
            name: 'settings',
            component: () =>
              import(
                /* webpackChunkName: "hubsOverview" */ '../views/settings/Settings.vue'
              ),
          },
          {
            path: 'people',
            name: 'peopleOverview',
            component: () =>
              import('../views/people/people-overview/PeopleOverview.vue'),
          },
          {
            path: 'people/add',
            name: 'addPerson',
            component: () => import('../views/people/add-person/AddPerson.vue'),
          },
          {
            path: 'people/upload/people',
            name: 'uploadPeople',
            component: () =>
              import('../views/people/upload/people/UploadPeople.vue'),
          },
          {
            path: 'people/upload/dependents',
            name: 'uploadDependents',
            component: () =>
              import('../views/people/upload/dependents/UploadDependents.vue'),
          },
          {
            path: String.raw`people/:personId(\d+)/details`,
            name: 'personDetails',
            props: true,
            component: () =>
              import('../views/people/person-details/PersonDetails.vue'),
          },
          {
            path: String.raw`people/:personId(\d+)/edit`,
            name: 'editPerson',
            props: true,
            component: () =>
              import('../views/people/edit-person/EditPerson.vue'),
          },
          {
            path: 'businesses',
            name: 'businessesOverview',
            component: () =>
              import('../views/businesses/businesses-overview/BusinessesOverview.vue'),
          },
          {
            path: 'budgets',
            name: 'budgetsOverview',
            component: () =>
              import('../views/businesses/budgets-overview/BudgetsOverview.vue'),
          },
          {
            path: 'businesses/add',
            name: 'addBusiness',
            component: () =>
              import('../views/businesses/add-business/AddBusiness.vue'),
          },
          {
            path: String.raw`businesses/:businessId(\d+)/details`,
            name: 'businessDetails',
            props: true,
            component: () =>
              import('../views/businesses/business-details/BusinessDetails.vue'),
          },
          {
            path: String.raw`businesses/:businessId(\d+)/edit`,
            name: 'editBusiness',
            props: true,
            component: () =>
              import('../views/businesses/edit-business/EditBusiness.vue'),
          },
          {
            path: 'people/pending',
            name: 'pendingUsers',
            component: () => import('../views/people/pending/PendingUsers.vue'),
          },
          {
            path: 'promote',
            name: 'promote',
            beforeEnter: blockPromoteWithoutCoOp,
            component: () => import('../views/promote/PromoteView.vue'),
          },
          {
            path: 'promote/settings',
            name: 'promoteSettings',
            beforeEnter: blockPromoteWithoutCoOp,
            component: () =>
              import('../views/promote/promote-settings/PromoteSettingsView.vue'),
          },
          {
            path: 'profile',
            name: 'profile',
            component: () => import('../views/profile/Profile.vue'),
          },
          {
            path: 'print',
            name: 'print',
            component: () => import('@/views/prints/PrintsView.vue'),
            children: [
              {
                path: '',
                name: 'prints-available',
                component: () => import('@/views/prints/AvailablePrints.vue'),
              },
              {
                path: 'history',
                name: 'prints-history',
                component: () => import('@/views/prints/PrintHistory.vue'),
              },
            ],
          },

          {
            path: 'learning',
            name: 'learningOverview',
            redirect: (to) => ({
              path: `/${to.params.organizationSlug}/learning/modules`,
            }),
          },
          {
            path: 'learning/curriculums/create',
            name: 'createLearningCurriculum',
            component: () =>
              import('../views/learning/create-curriculum/CreateCurriculum.vue'),
          },
          {
            path: String.raw`learning/curriculums/:curriculumId(\d+)/details`,
            name: 'learningCurriculumDetails',
            props: true,
            component: () =>
              import('../views/learning/curriculum-details/CurriculumDetails.vue'),
          },
          {
            path: String.raw`learning/curriculums/:curriculumId(\d+)/edit`,
            name: 'editLearningCurriculum',
            props: true,
            component: () =>
              import('../views/learning/edit-curriculum/EditCurriculum.vue'),
          },
          {
            path: 'learning/modules',
            name: 'learningModulesOverview',
            component: () =>
              import('../views/learning/modules-overview/ModulesOverview.vue'),
          },
          {
            path: 'learning/modules/create',
            name: 'createLearningModule',
            component: () =>
              import('../views/learning/create-module/CreateModule.vue'),
          },
          {
            path: String.raw`learning/modules/:moduleId(\d+)/details`,
            name: 'learningModuleDetails',
            props: true,
            component: () =>
              import('../views/learning/module-details/ModuleDetails.vue'),
          },
          {
            path: String.raw`learning/modules/:moduleId(\d+)/edit`,
            name: 'editLearningModule',
            props: true,
            component: () =>
              import('../views/learning/edit-module/EditModule.vue'),
          },
          {
            path: 'learning/activities',
            name: 'learningActivitiesOverview',
            component: () =>
              import('../views/learning/activities-overview/ActivitiesOverview.vue'),
          },
          {
            path: 'learning/activities/create',
            name: 'createLearningActivity',
            component: () =>
              import('../views/learning/create-activity/CreateActivity.vue'),
          },
          {
            path: String.raw`learning/activities/:activityId(\d+)/details`,
            name: 'learningActivityDetails',
            props: true,
            component: () =>
              import('../views/learning/activity-details/ActivityDetails.vue'),
          },
          {
            path: String.raw`learning/activities/:activityId(\d+)/edit`,
            name: 'editLearningActivity',
            props: (route) => ({
              ...route.params,
              moduleId: route.query.moduleId,
            }),
            component: () =>
              import('../views/learning/edit-activity/EditActivity.vue'),
          },
          {
            path: String.raw`learning/activities/:activityId(\d+)/preview`,
            name: 'learningActivityPreview',
            props: (route) => ({
              ...route.params,
              moduleId: route.query.moduleId,
            }),
            component: () =>
              import('../views/learning/activity-preview/ActivityPreview.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/super-admin',
    name: 'superAdmin',
    component: () =>
      import('../layouts/super-admin-layout/super-admin-layout.vue'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'organizations',
        name: 'organizations',
        component: () =>
          import('../views/super-admin/organizations/organizations.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('../views/NotFound.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.onError((err) => {
  const { handleError } = useErrorStore();

  handleError(router, err);
});

router.beforeEach(async (to, from, next) => {
  try {
    if (from.matched.length > 0) {
      trackPageChange(to.fullPath);
    }

    const authUserStore = useAuthUserStore();

    const { loadCurrentOrganization } = useCurrentOrganizationStore();

    const organizationSlug = getFirstPathParam(to.params.organizationSlug);

    if (authUserStore.token && organizationSlug) {
      // When token exists, run auth and org loading in parallel.
      // Both use the token from localStorage via the API interceptor.
      await Promise.all([
        authUserStore.initializeAuthUser(),
        loadCurrentOrganization(organizationSlug),
      ]);
    } else {
      await authUserStore.initializeAuthUser();

      if (
        organizationSlug &&
        authUserStore.authUser !== null &&
        !authUserStore.authUser.isSuperAdmin
      ) {
        await loadCurrentOrganization(organizationSlug);
      }
    }

    const requiresAuth = to.matched.some((x) => x.meta.requiresAuth);
    const authForbidden = to.matched.some((x) => x.meta.authForbidden);

    const isLoggedIn = authUserStore.authUser !== null;

    if (requiresAuth && !isLoggedIn) {
      next({
        path: '/auth/login',
        query: to.name === 'logout' ? undefined : { redirect: to.fullPath },
      });
    } else if (authForbidden && isLoggedIn) {
      next('/');
    } else if (
      to.path !== '/super-admin/organizations' &&
      to.path !== '/auth/logout' &&
      authUserStore.authUser?.isSuperAdmin
    ) {
      next('/super-admin/organizations');
    } else {
      next();
    }
  } catch (err) {
    next(new Error('Vue-Router error', { cause: err }));
  }
});

export default router;
